import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router';

import { addTime } from 'modules/elapsedTime/actions';
import { finalizeSelfService } from 'modules/selfServices/actions';

import { isPublicDevice } from 'modules/dealers/selectors';
import { getSelectedSelfServiceId } from 'modules/auth/selectors';
import { isLogoutModalDisplayed } from 'modules/sockets/selectors';
import { getStepsConfig } from 'modules/selfServices/stepsConfigSelectors';

import { useDispatch } from 'hooks';
import useCurrentStep from 'hooks/useCurrentStep';
import { getUrlParam } from 'utils/urlUtils';
import useElapsedTime from 'hooks/useElapsedTime';
import { toUrlSlug, fromUrlSlug } from 'utils/stepUrlUtils';
import { usePatchDropOffPointMutation } from 'modules/selfServices/service';
import { useSubmitFinalizationMutation } from 'modules/selfServices/finalizeService';
import { FINALIZE_FIXED_CACHE_KEY } from 'modules/selfServices/constants';
import { useGetSelfServiceByIdQuery } from 'modules/selfServices/service';
import { INACTIVITY_SELF_SERVICE_FLOWS, INACTIVITY_TIME_ON_PAYMENT } from 'modules/auth/constants';

import { Spinner } from 'components/ui';
import { isOpened } from 'modules/kiosk/selectors';
import { createDictionary } from 'utils/objectUtils';
import SocketRedirectModal from 'components/SocketRedirectModal';
import { SelfServiceSteps } from 'modules/selfServices/types/SelfServiceSteps';
import { upsertFormDebounce } from 'components/ui/ConnectedForm/decorator';

import Inactivity from './Inactivity';
import type { SelfServiceStep, SelfServiceOutletContext } from './types';
import UpdateLogoutModal from '../UpdateLogoutModal';
import { getSelfServiceSteps } from './Steps';
import { isNotAnswered } from './Steps/utils';

const STEPS_TO_FINALIZE_AT_INIT = [SelfServiceSteps.PARTTEAM_KEY, SelfServiceSteps.SHAREBOX_KEY, SelfServiceSteps.FINAL_INSTRUCTIONS];

const SelfServiceLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { step: stepSlug } = useParams<{ step: string }>();
  const dispatch = useDispatch();
  const { getElapsedTime, reset } = useElapsedTime();

  const config = useSelector(getStepsConfig);

  const currentStep = useCurrentStep();
  const doorsOpened = useSelector(isOpened);
  const isPublic = useSelector(isPublicDevice);
  const displayModal = useSelector(isLogoutModalDisplayed);
  const selfServiceId = useSelector(getSelectedSelfServiceId);

  const {
    data, isLoading, isSuccess, isError, isFetching,
  } = useGetSelfServiceByIdQuery({ id: selfServiceId });
  const [patchDropOffPoint] = usePatchDropOffPointMutation();
  const [, { isUninitialized: isFinalizeUninitialized }] = useSubmitFinalizationMutation({
    fixedCacheKey: FINALIZE_FIXED_CACHE_KEY,
  });

  const displayLoading = isLoading || isFetching;

  const adjustedConfig = React.useMemo(() => {
    if (stepSlug && fromUrlSlug(stepSlug) === SelfServiceSteps.BASKET) {
      return { ...config, displayBasket: true };
    }
    return config;
  }, [config, stepSlug]);

  const steps = React.useMemo(() => (
    data ? getSelfServiceSteps(data, adjustedConfig) : []
  ), [data, adjustedConfig]);

  const setDropOffPoint = React.useCallback((key: string) => {
    if (key) {
      patchDropOffPoint({ step: key, selfServiceId });
    }
  }, [patchDropOffPoint, selfServiceId]);

  // Redirect to first step if no step in URL or if current step is not visible
  React.useEffect(() => {
    if (steps.length > 0) {
      const isStepVisible = stepSlug && steps.some(({ key }) => key === fromUrlSlug(stepSlug));
      if (!isStepVisible) {
        console.log('[layout] - redirecting to first step');
        navigate(`${toUrlSlug(steps[0].key)}${location.search}`, {
          replace: true,
          state: location.state,
        });
      }
    }
  }, [steps, stepSlug, navigate, location.search, location.state]);

  const shouldFinalizeAtInit = data !== undefined
    && isNotAnswered(data.status)
    && STEPS_TO_FINALIZE_AT_INIT.includes(steps[0]?.key)
    && isFinalizeUninitialized;

  // Auto-finalize on init for specific steps
  React.useEffect(() => {
    if (shouldFinalizeAtInit) {
      dispatch(finalizeSelfService(getElapsedTime()));
    }
  }, [shouldFinalizeAtInit, dispatch, getElapsedTime]);

  const handleStepChange = React.useCallback((newStep: SelfServiceStep) => {
    window.scrollTo(0, 1); // Force scroll to top on step change
    upsertFormDebounce.flush();
    setDropOffPoint(newStep.apiKey);

    const prevStep = steps.find(({ key }) => key === newStep.previous);
    if (!prevStep?.data?.isFinalizeStep) {
      dispatch(addTime({ key: newStep.key, time: getElapsedTime() }));
    }
    reset();
  }, [setDropOffPoint, steps, reset, dispatch, getElapsedTime]);

  const handleFinalize = React.useCallback(() => {
    upsertFormDebounce.flush();
    dispatch(finalizeSelfService(getElapsedTime()));
  }, [dispatch, getElapsedTime]);

  const shouldDisplaySpinner = displayLoading || shouldFinalizeAtInit;
  const shouldRenderOutlet = !shouldDisplaySpinner && isSuccess && steps.length > 0 && Boolean(stepSlug);
  const shouldUseInactivity = isPublic || currentStep === SelfServiceSteps.FINAL_INVOICE;
  const isInactivityPaused = currentStep === SelfServiceSteps.PARTTEAM_KEY && doorsOpened;
  const inactivitySecondsByStep = createDictionary<string, number>({
    [SelfServiceSteps.FINAL_INVOICE]: INACTIVITY_TIME_ON_PAYMENT,
  }, INACTIVITY_SELF_SERVICE_FLOWS);

  if (!selfServiceId || isError) {
    const pathToken = getUrlParam('token');
    return (<Navigate to={`/?token=${pathToken}`} />);
  }

  const outletContext: SelfServiceOutletContext = {
    steps,
    onStepChange: handleStepChange,
    onFinalize: handleFinalize,
  };

  return (
    <div>
      {shouldDisplaySpinner && <Spinner className="homePage" />}
      {shouldRenderOutlet && <Outlet context={outletContext} />}
      <SocketRedirectModal />
      {displayModal && <UpdateLogoutModal />}
      {shouldUseInactivity && <Inactivity seconds={inactivitySecondsByStep[currentStep]} paused={isInactivityPaused} />}
    </div>
  );
};

export default SelfServiceLayout;
